import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { getLead } from "@/api/leads";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { fmtDateTime, fmtMoney } from "@/lib/format";

export function LeadDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => getLead(id),
    enabled: !!id,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lead detail"
        subtitle={id}
        actions={
          <Link to="/leads" className="btn-secondary">
            ← Back to leads
          </Link>
        }
      />

      {error && <ErrorBanner error={error} />}
      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner /> Loading…
        </div>
      )}

      {data && (
        <>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Summary</div>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Name"
                  value={
                    [data.client_name_first, data.client_name_last]
                      .filter(Boolean)
                      .join(" ") || "—"
                  }
                />
                <Field label="Email" value={data.client_email ?? "—"} />
                <Field label="Phone" value={data.client_phone ?? "—"} />
                <Field
                  label="Phone verified"
                  value={
                    data.number_verified === null
                      ? "—"
                      : data.number_verified
                        ? "Yes"
                        : "No"
                  }
                />
                <Field label="State" value={data.client_state ?? "—"} />
                <Field label="Amount" value={fmtMoney(data.client_amount)} />
                <Field label="UTM source" value={data.utm_source ?? "—"} />
                <Field
                  label="Sugar case id"
                  value={data.sugar_case_id ?? "—"}
                />
                <Field
                  label="Sugar status"
                  value={data.sugar_status ?? "—"}
                />
                <Field
                  label="Created at"
                  value={fmtDateTime(data.created_at)}
                />
                <Field
                  label="Modified at"
                  value={fmtDateTime(data.modified_at)}
                />
                <Field
                  label="Fetched at"
                  value={fmtDateTime(data.fetched_at)}
                />
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Raw payload (cerodeuda JSON)</div>
              <span className="text-xs text-slate-500">
                {Object.keys(data.raw).length} top-level keys
              </span>
            </div>
            <div className="card-body">
              <pre className="max-h-[60vh] overflow-auto rounded-md bg-slate-900 p-4 text-xs leading-relaxed text-slate-100">
                {JSON.stringify(data.raw, null, 2)}
              </pre>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-slate-900">{value}</dd>
    </div>
  );
}
